// lib/api/audio-analysis.ts
// Web Audio API를 사용한 실제 음악 분석

import { createAudioContext } from "../utils/audio-context";

export interface AudioAnalysisData {
  tempo: number; // BPM
  pitchVariation: number; // 음정 변화 (0-1)
  energyLevel: number; // 에너지 레벨 (0-1)
  rhythmConsistency: number; // 리듬 일관성 (0-1)
  dynamicRange: number; // 다이내믹 레인지 (0-1)
  spectralCentroid: number; // 스펙트럴 중심 (톤 밝기)
  duration: number; // 초
}

export type AudioAnalysisResult = AudioAnalysisData & {
  pitchAccuracy?: number;
  mood?: string;
  emotion?: string;
  confidence?: number;
};

/**
 * 오디오 파일을 분석하여 객관적인 데이터 추출
 */
export async function analyzeAudioFile(
  audioFile: File
): Promise<AudioAnalysisData> {
  return new Promise((resolve, reject) => {
    const audioContext = createAudioContext();
    
    if (!audioContext) {
      reject(new Error("브라우저가 Web Audio API를 지원하지 않습니다"));
      return;
    }
    
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // 오디오 데이터 추출
        const channelData = audioBuffer.getChannelData(0); // 모노 또는 첫 번째 채널
        const sampleRate = audioBuffer.sampleRate;
        const duration = audioBuffer.duration;

        // 1. 템포 분석 (BPM)
        const tempo = analyzeTempo(channelData, sampleRate);

        // 2. 음정 변화 분석
        const pitchVariation = analyzePitchVariation(channelData);

        // 3. 에너지 레벨 분석
        const energyLevel = analyzeEnergyLevel(channelData);

        // 4. 리듬 일관성 분석
        const rhythmConsistency = analyzeRhythmConsistency(
          channelData,
          sampleRate
        );

        // 5. 다이내믹 레인지 분석
        const dynamicRange = analyzeDynamicRange(channelData);

        // 6. 스펙트럴 중심 (톤 밝기)
        const spectralCentroid = analyzeSpectralCentroid(
          channelData,
          sampleRate
        );

        resolve({
          tempo,
          pitchVariation,
          energyLevel,
          rhythmConsistency,
          dynamicRange,
          spectralCentroid,
          duration,
        });
      } catch (error) {
        reject(new Error("오디오 분석 실패: " + error));
      }
    };

    reader.onerror = () => reject(new Error("파일 읽기 실패"));
    reader.readAsArrayBuffer(audioFile);
  });
}

/**
 * 템포 분석 (BPM)
 * 에너지 피크를 감지하여 BPM 추정
 */
function analyzeTempo(data: Float32Array, sampleRate: number): number {
  const peaks: number[] = [];
  const threshold = 0.3; // 피크 감지 임계값

  // 피크 감지
  for (let i = 1; i < data.length - 1; i++) {
    if (
      Math.abs(data[i]) > threshold &&
      Math.abs(data[i]) > Math.abs(data[i - 1]) &&
      Math.abs(data[i]) > Math.abs(data[i + 1])
    ) {
      peaks.push(i);
    }
  }

  if (peaks.length < 2) return 120; // 기본값

  // 피크 간 평균 간격 계산
  const intervals: number[] = [];
  for (let i = 1; i < peaks.length; i++) {
    intervals.push(peaks[i] - peaks[i - 1]);
  }

  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  const secondsPerBeat = avgInterval / sampleRate;
  const bpm = Math.round(60 / secondsPerBeat);

  // BPM 범위 제한 (60-180)
  return Math.max(60, Math.min(180, bpm));
}

/**
 * 음정 변화 분석
 * Zero-crossing rate를 사용하여 음정 변화 측정
 */
function analyzePitchVariation(data: Float32Array): number {
  let zeroCrossings = 0;

  for (let i = 1; i < data.length; i++) {
    if (
      (data[i - 1] >= 0 && data[i] < 0) ||
      (data[i - 1] < 0 && data[i] >= 0)
    ) {
      zeroCrossings++;
    }
  }

  // 정규화 (0-1)
  const normalized = Math.min(1, zeroCrossings / (data.length * 0.1));
  return parseFloat((1 - normalized).toFixed(2)); // 높을수록 안정적
}

/**
 * 에너지 레벨 분석
 * RMS (Root Mean Square) 사용
 */
function analyzeEnergyLevel(data: Float32Array): number {
  let sum = 0;

  for (let i = 0; i < data.length; i++) {
    sum += data[i] * data[i];
  }

  const rms = Math.sqrt(sum / data.length);
  return Math.min(1, rms * 10); // 정규화
}

/**
 * 리듬 일관성 분석
 * 에너지 변화의 일관성 측정
 */
function analyzeRhythmConsistency(
  data: Float32Array,
  sampleRate: number
): number {
  const windowSize = Math.floor(sampleRate * 0.1); // 100ms 윈도우
  const energies: number[] = [];

  for (let i = 0; i < data.length; i += windowSize) {
    let sum = 0;
    const end = Math.min(i + windowSize, data.length);

    for (let j = i; j < end; j++) {
      sum += data[j] * data[j];
    }

    energies.push(Math.sqrt(sum / (end - i)));
  }

  // 에너지 변화의 표준편차 계산
  const mean = energies.reduce((a, b) => a + b, 0) / energies.length;
  const variance =
    energies.reduce((sum, e) => sum + (e - mean) ** 2, 0) / energies.length;
  const stdDev = Math.sqrt(variance);

  // 일관성 = 1 - (표준편차 / 평균)
  const consistency = Math.max(0, 1 - stdDev / mean);
  return parseFloat(consistency.toFixed(2));
}

/**
 * 다이내믹 레인지 분석
 * 최대/최소 진폭의 비율
 */
function analyzeDynamicRange(data: Float32Array): number {
  let max = 0;
  let min = Infinity;

  for (let i = 0; i < data.length; i++) {
    const abs = Math.abs(data[i]);
    if (abs > max) max = abs;
    if (abs < min && abs > 0.01) min = abs; // 노이즈 제거
  }

  const range = max / (min || 0.01);
  return Math.min(1, range / 100); // 정규화
}

/**
 * 스펙트럴 중심 분석 (톤 밝기)
 * FFT를 사용하여 주파수 분포 분석
 */
function analyzeSpectralCentroid(
  data: Float32Array,
  sampleRate: number
): number {
  // 간단한 근사: 고주파 에너지 비율
  const nyquist = sampleRate / 2;
  const highFreqThreshold = Math.min(nyquist, 4000); // 4kHz 이상을 고주파로 간주
  const highFreqStart = Math.floor(
    data.length * (highFreqThreshold / (nyquist || 1))
  );
  let highFreqEnergy = 0;
  let totalEnergy = 0;

  for (let i = 0; i < data.length; i++) {
    const energy = data[i] * data[i];
    totalEnergy += energy;

    if (i >= highFreqStart) {
      highFreqEnergy += energy;
    }
  }

  const centroid = highFreqEnergy / (totalEnergy || 1);
  return parseFloat(centroid.toFixed(2));
}

/**
 * 오디오 분석 데이터를 VocalAnalysisResult로 변환 (개선된 버전)
 */
export function convertToVocalAnalysis(audioData: AudioAnalysisData): {
  pitchAccuracy: number;
  rhythmStability: number;
  energyDynamics: number;
  emotionalExpression: number;
  toneConsistency: number;
} {
  // 🎯 개선된 점수 변환 로직: 실제 음악적 특성을 더 잘 반영

  // 1. 음정 정확도: pitchVariation이 낮을수록(안정적) 높은 점수
  // 변환 범위를 더 세밀하게 조정 (60-95점 범위로 분포)
  const pitchBase = (1 - audioData.pitchVariation) * 100;
  const pitchAccuracy = Math.round(
    Math.max(55, Math.min(95, pitchBase * 0.9))
  );

  // 2. 리듬 안정성: rhythmConsistency를 그대로 활용하되, 템포에 따라 보정
  // 빠른 템포일수록 리듬 유지가 어려우므로 보정
  const tempoFactor =
    audioData.tempo > 140 ? 0.85 : audioData.tempo < 80 ? 0.9 : 1.0;
  const rhythmBase = audioData.rhythmConsistency * 100 * tempoFactor;
  const rhythmStability = Math.round(
    Math.max(50, Math.min(95, rhythmBase))
  );

  // 3. 에너지 다이내믹: energyLevel과 dynamicRange를 복합적으로 고려
  // 에너지가 너무 낮거나 너무 높으면 감점
  const energyOptimal = 1 - Math.abs(audioData.energyLevel - 0.6) * 1.5;
  const dynamicBonus = audioData.dynamicRange * 30; // 다이내믹 레인지 보너스
  const energyDynamics = Math.round(
    Math.max(50, Math.min(95, energyOptimal * 70 + dynamicBonus))
  );

  // 4. 감정 표현력: 다이내믹 레인지 + 스펙트럴 변화 + 템포 특성
  // 다양한 요소를 종합적으로 고려
  const dynamicScore = audioData.dynamicRange * 60;
  const spectralVariety = audioData.spectralCentroid * 30; // 스펙트럴 다양성
  const tempoExpression =
    audioData.tempo > 100 && audioData.tempo < 140 ? 10 : 5;
  const emotionalExpression = Math.round(
    Math.max(55, Math.min(95, dynamicScore + spectralVariety + tempoExpression))
  );

  // 5. 톤 일관성: 스펙트럴 안정성 + 에너지 일관성
  // spectralCentroid가 0.3-0.7 범위에 있으면 좋은 톤
  const spectralStability =
    1 - Math.abs(audioData.spectralCentroid - 0.5) * 1.5;
  const energyStability = audioData.rhythmConsistency; // 리듬 일관성도 톤에 영향
  const toneConsistency = Math.round(
    Math.max(50, Math.min(95, spectralStability * 60 + energyStability * 40))
  );

  // 🎵 최종 검증: 모든 점수가 적절한 범위(50-95)에 있도록
  return {
    pitchAccuracy: ensureRange(pitchAccuracy, 55, 95),
    rhythmStability: ensureRange(rhythmStability, 50, 95),
    energyDynamics: ensureRange(energyDynamics, 50, 95),
    emotionalExpression: ensureRange(emotionalExpression, 55, 95),
    toneConsistency: ensureRange(toneConsistency, 50, 95),
  };
}

/**
 * 점수가 적절한 범위 내에 있도록 보장
 */
function ensureRange(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}
